<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import InvoicesList from './InvoicesList.vue'
import { useSectionTour } from '@/composables/useSectionTour'
import type { Contract } from '@/@core/stores/contract'
import { useContractStore } from '@/@core/stores/contract'
import axios from '@/api/axios'
import API_ENDPOINTS from '@/api/client'
import { track } from '@/boot/mixpanel'
import { useAuthStore } from '@/utils/auth'

definePage({
  meta: {
    authenticatedOnly: true,
  },
})

const { t } = useI18n()
const contracts = ref<Contract[]>([])
const isLoading = ref(true)
const sortBy = [{ key: 'contract_id', order: 'desc' } as const]
const loadingMessage = ref('')
const contractStore = useContractStore()
const authStore = useAuthStore()

const previewDialog = ref(false)
const previewUrl = ref('')
const isMobile = ref(false)
const previewId = ref('')

const previewContractId = ref('')

const filterType = ref<'PENDING' | 'COMPLETED' | 'CANCELLED'>('PENDING')

watch(filterType, newFilter => {
  if (newFilter === 'COMPLETED')
    contracts.value = contractStore.completedContracts

  else if (newFilter === 'CANCELLED')
    contracts.value = contractStore.cancelledContracts

  else if (newFilter === 'PENDING')
    contracts.value = contractStore.pendingContracts

  track('Contract filter changed', {
    filter: newFilter,
    user: authStore.user,
  })
})

const getContracts = async () => {
  loadingMessage.value = t('Loading contracts...')
  isLoading.value = true

  try {
    await contractStore.fetchAllContracts(authStore.user.id, authStore.access_token)
    contracts.value = contractStore.pendingContracts
  }
  catch (error) {
    console.error('Failed to fetch contracts:', error)
  }
  finally {
    loadingMessage.value = ''
    isLoading.value = false
  }
}

const downloadContract = async (id: string, contract_id: string) => {
  loadingMessage.value = t('Downloading contract...')
  isLoading.value = true
  try {
    const url = `${API_ENDPOINTS.DOWNLOAD_CONTRACT}${id}`

    const res = await axios.get(url, {
      responseType: 'blob',
    })

    const blob = new Blob([res.data], { type: 'application/pdf' })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = downloadUrl
    link.setAttribute('download', `${contract_id}.pdf`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  catch (error) {
    console.error('Error downloading the contract:', error)
  }
  finally {
    loadingMessage.value = ''
    isLoading.value = false
    track('Downloaded contract', {
      contract_id,
    })
  }
}

const previewContract = async (id: string, contract_id: string) => {
  loadingMessage.value = t('Loading contract...')
  isLoading.value = true
  try {
    const res = await axios.get(API_ENDPOINTS.PREVIEW_CONTRACT(id), {
      responseType: 'blob',
    })

    const blob = new Blob([res.data], { type: 'application/pdf' })
    const fileURL = URL.createObjectURL(blob)

    if (isMobile.value) {
      window.open(`${axios.defaults.baseURL?.replace(/\/$/, '')}${API_ENDPOINTS.PREVIEW_CONTRACT(id)}`, '_blank')
    }
    else {
      previewUrl.value = fileURL
      previewDialog.value = true
    }

    previewContractId.value = contract_id
    previewId.value = id
  }
  catch (error) {
    console.error('Error previewing the contract:', error)
  }
  finally {
    loadingMessage.value = ''
    isLoading.value = false
    track('Previewed contract', {
      contract_id,
    })
  }
}

const { startIfFirstTime } = useSectionTour('contracts', 'v1', [
  {
    selector: '[data-tour="contracts-tabs"]',
    title: t('tutorials.contracts.step1.title'),
    text: t('tutorials.contracts.step1.text'),
  },
  {
    selector: '[data-tour="contracts-search"]',
    title: t('tutorials.contracts.step2.title'),
    text: t('tutorials.contracts.step2.text'),
    position: 'left',
  },
  {
    selector: '[data-tour="contracts-table"]',
    title: t('tutorials.contracts.step3.title'),
    text: t('tutorials.contracts.step3.text'),
    position: 'top',
  },
  {
    selector: '[data-tour="contracts-actions"]',
    title: t('tutorials.contracts.step4.title'),
    text: t('tutorials.contracts.step4.text'),
    position: 'left',
  },
])

onMounted(() => {
  getContracts()
  isMobile.value = window.innerWidth <= 1280
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 1280
  })
  startIfFirstTime()
})
</script>

<template>
  <InvoicesList
    v-model:filter-type="filterType"
    v-model:sort-by="sortBy"
    :contracts="contracts"
    :is-loading="isLoading"
    :loading-message="loadingMessage"
    @download-button-clicker="downloadContract"
    @preview-button-clicker="previewContract"
  />

  <VDialog
    v-model="previewDialog"
    :width="isMobile ? '95%' : '80%'"
    :max-height="isMobile ? '90vh' : '85vh'"
    persistent
    transition="dialog-bottom-transition"
  >
    <VCard
      class="preview-card"
      :class="{ mobile: isMobile }"
    >
      <VCardTitle class="d-flex justify-space-between align-center">
        {{ t('Preview contract') }}
        <VBtn
          icon
          @click="previewDialog = false"
        >
          <VIcon icon="tabler-x" />
        </VBtn>
      </VCardTitle>

      <VCardText class="preview-card-text">
        <iframe
          v-if="previewUrl"
          :src="`${axios.defaults.baseURL?.replace(/\/$/, '')}${API_ENDPOINTS.PREVIEW_CONTRACT(previewId)}`"
          style="display: block; border: none; block-size: 80vh; inline-size: 100%;"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.preview-card.mobile {
  margin: 0.5rem !important;
}

.preview-card-text {
  padding: 0 !important;
}
</style>
